import React, { useMemo, useState } from 'react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { AlertTriangle, CheckCircle, Clock, Upload, X, Trash2, RefreshCw, Image as ImageIcon } from 'lucide-react';
import { useAppStore } from '../store';
import { useAuth } from '../context/AuthContext';

const FichasAprobacion: React.FC = () => {
  const { fichas, fetchFichas, addFicha, updateFichaStatus, fetchFichaHistory, fetchFichasHistoryAll, deleteFicha, replaceFichaFile } = useAppStore();
  const safeFichas = Array.isArray(fichas) ? fichas : [];
  const { user, userRole } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadKind, setUploadKind] = useState<'produccion'|'muestra'>('produccion');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [timeFilter, setTimeFilter] = useState<'7d'|'30d'|'90d'|'all'>('30d');
  const [kindFilter, setKindFilter] = useState<'all'|'produccion'|'muestra'>('all');
  const [sellerFilter, setSellerFilter] = useState('');

  // Modal states
  const [approveModal, setApproveModal] = React.useState<{open: boolean; ficha: any | null}>({ open: false, ficha: null });
  const [rejectModal, setRejectModal] = React.useState<{open: boolean; ficha: any | null}>({ open: false, ficha: null });
  const [rejectComment, setRejectComment] = React.useState('');
  const [rejectImages, setRejectImages] = React.useState<File[]>([]);
  const [historyModal, setHistoryModal] = React.useState<{open: boolean; items: any[]}>({ open: false, items: [] });
  const [correctionsModal, setCorrectionsModal] = React.useState<{open:boolean; ficha:any|null; items:any[]}>({ open:false, ficha:null, items: [] });
  const [deleteConfirm, setDeleteConfirm] = React.useState<{open:boolean; id:string|null; title:string}>({ open:false, id:null, title:'' });
  const [isUpdatingStatus, setIsUpdatingStatus] = React.useState(false);
  const [replaceDone, setReplaceDone] = React.useState<{open:boolean; title:string}>({ open:false, title:'' });
  const [analyticsOpen, setAnalyticsOpen] = React.useState(false);
  const [analyticsSeller, setAnalyticsSeller] = React.useState('');
  const [analyticsPeriod, setAnalyticsPeriod] = React.useState<'7d'|'30d'|'90d'|'all'>('30d');
  const [allHistory, setAllHistory] = React.useState<any[]>([]);

  // Funciones para manejar aprobación/desaprobación con protección contra doble clic
  const handleApprove = async () => {
    if (isUpdatingStatus || !approveModal.ficha) return;
    
    setIsUpdatingStatus(true);
    try {
      await updateFichaStatus(approveModal.ficha.id, 'approved');
      setApproveModal({ open: false, ficha: null });
    } catch (error) {
      console.error('Error aprobando ficha:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleReject = async () => {
    if (isUpdatingStatus || !rejectModal.ficha) return;
    
    setIsUpdatingStatus(true);
    try {
      const imageUrls = await uploadRejectImages();
      await updateFichaStatus(rejectModal.ficha.id, 'rejected', rejectComment, imageUrls);
      setRejectModal({ open: false, ficha: null });
      setRejectComment('');
      setRejectImages([]);
    } catch (error) {
      console.error('Error desaprobando ficha:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  React.useEffect(() => { fetchFichas(); }, [fetchFichas]);

  const canMove = userRole === 'Producción' || userRole === 'Administrador';
  const canCreate = userRole === 'Vendedor' || canMove;

  const filtered = useMemo(()=>{
    const now = new Date();
    const from = new Date(now);
    if (timeFilter === '7d') from.setDate(from.getDate()-7);
    else if (timeFilter === '30d') from.setDate(from.getDate()-30);
    else if (timeFilter === '90d') from.setDate(from.getDate()-90);
    else from.setFullYear(1970);
    return safeFichas.filter(f => {
      const created = new Date(f.created_at);
      if (!(created >= from)) return false;
      if (kindFilter !== 'all' && (f.kind || 'produccion') !== kindFilter) return false;
      if (sellerFilter && !(f.vendedor_email||'').toLowerCase().includes(sellerFilter.toLowerCase())) return false;
      return true;
    });
  }, [safeFichas, timeFilter, kindFilter, sellerFilter]);

  const columns = useMemo(() => ({
    in_review: filtered.filter(f => (f.status === 'in_review' || f.status === 'rejected') && (canMove || f.vendedor_id === user?.id)),
    approved: filtered.filter(f => f.status === 'approved' && (canMove || f.vendedor_id === user?.id)),
  }), [filtered, user?.id, canMove]);

  const openHistory = async (fichaId: string) => {
    const items = await fetchFichaHistory(fichaId);
    setHistoryModal({ open: true, items });
  };

  const startReplaceFile = async (e: React.ChangeEvent<HTMLInputElement>, f: any) => {
    if (!e.target.files || !user) return;
    const file = e.target.files[0];
    const fileName = `${user.id}-${Date.now()}-${file.name}`;
    const { error: upErr } = await (await import('../lib/supabase')).supabase.storage
      .from('fichas')
      .upload(fileName, file, { upsert: true, contentType: 'application/pdf' });
    if (upErr) { console.error(upErr); return; }
    const { data: urlData } = (await (await import('../lib/supabase')).supabase.storage.from('fichas').getPublicUrl(fileName)) as any;
    await replaceFichaFile(f.id, urlData.publicUrl);
    setReplaceDone({ open: true, title: f.title });
  };

  const startUploadFlow = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const file = e.target.files[0];
    setUploadFile(file);
    setUploadTitle(file ? file.name.replace(/\.pdf$/i, '') : '');
    setShowUploadModal(true);
    e.target.value = '';
  };

  const openCorrectionsModal = async (fichaId: string) => {
    const items = await fetchFichaHistory(fichaId);
    const rejectedItems = (items || []).filter((i:any)=> i.status === 'rejected');
    setCorrectionsModal({ open: true, ficha: safeFichas.find(f=>f.id===fichaId) || null, items: rejectedItems });
  };

  const confirmUpload = async () => {
    if (!uploadFile || !user) return;
    setIsUploading(true);
    try {
      const fileName = `${user.id}-${Date.now()}-${uploadFile.name}`;
      const { error: upErr } = await (await import('../lib/supabase')).supabase.storage
        .from('fichas')
        .upload(fileName, uploadFile, { upsert: true, contentType: 'application/pdf' });
      if (upErr) throw upErr;
      const { data: urlData } = (await (await import('../lib/supabase')).supabase.storage.from('fichas').getPublicUrl(fileName)) as any;
      await addFicha({ title: uploadTitle || uploadFile.name.replace(/\.pdf$/i, ''), pdf_url: urlData.publicUrl, vendedor_id: user.id, kind: uploadKind });
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadTitle('');
      setUploadKind('produccion');
    } catch (e) {
      console.error(e);
    } finally {
      setIsUploading(false);
    }
  };

  const openCorrections = async (fichaId: string) => {
    const items = await fetchFichaHistory(fichaId);
    const rejectedItems = (items || []).filter((i:any)=> i.status === 'rejected');
    setCorrectionsModal({ open: true, ficha: safeFichas.find(f=>f.id===fichaId) || null, items: rejectedItems });
  };

  const onRejectImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    setRejectImages(Array.from(e.target.files));
  };

  const uploadRejectImages = async (): Promise<string[]> => {
    if (!rejectImages.length || !user) return [];
    const supa = (await import('../lib/supabase')).supabase;
    const bucket = 'fichas-correcciones';
    const urls: string[] = [];
    for (const file of rejectImages) {
      const key = `${user.id}-${Date.now()}-${file.name}`;
      const { error } = await supa.storage.from(bucket).upload(key, file, { upsert: true, contentType: file.type });
      if (!error) {
        const { data } = supa.storage.from(bucket).getPublicUrl(key) as any;
        urls.push(data.publicUrl);
      }
    }
    return urls;
  };

  const getStatusMeta = (status: string): { label: string; icon: React.ReactNode; color: string } => {
    switch (status) {
      case 'approved':
        return { label: 'Aprobada', icon: <CheckCircle className="h-4 w-4 text-green-600" />, color: 'text-green-700' };
      case 'rejected':
        return { label: 'Desaprobada', icon: <AlertTriangle className="h-4 w-4 text-red-600" />, color: 'text-red-700' };
      case 'in_review':
      default:
        return { label: 'En revisión', icon: <Clock className="h-4 w-4 text-amber-600" />, color: 'text-amber-700' };
    }
  };

  const renderCard = (f: any) => {
    return (
      <div key={f.id} className={`p-4 rounded-lg border bg-white dark:bg-gray-800 shadow-sm ${ (f.rejected_comment && f.status !== 'approved') ? 'border-red-500' : f.status==='approved' ? 'border-green-500' : 'border-amber-400 dark:border-amber-500'}`}>
        <div className="flex justify-between items-start">
          <div className="space-y-1">
            <div className="text-base font-semibold flex items-center gap-2">
              {f.title}
              {f.kind && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${f.kind === 'muestra' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                  {f.kind === 'muestra' ? 'Muestra' : 'Producción'}
                </span>
              )}
            </div>
              <div className="text-xs text-gray-500">
              Por: {f.vendedor_email || '—'} · Creada: {new Date(f.created_at).toLocaleString('es-AR', { year: undefined as any, month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
            {f.updated_at && (
              <div className="text-xs text-gray-500">Última actualización: {new Date(f.updated_at).toLocaleString('es-AR', { year: undefined as any, month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {(userRole === 'Administrador' || userRole === 'Producción') && (
              <Button size="xs" variant="outline" onClick={() => openHistory(f.id)}>Historial</Button>
            )}
            {(userRole === 'Vendedor' && (f.rejected_comment && f.status !== 'approved')) && (
              <label className="inline-flex items-center gap-1 text-xs px-2 py-1 border rounded-md cursor-pointer">
                <RefreshCw className="h-3 w-3" /> Reemplazar
                <input type="file" accept="application/pdf" className="hidden" onChange={(e)=>startReplaceFile(e, f)} />
              </label>
            )}
            <button onClick={()=>setDeleteConfirm({open:true, id:f.id, title:f.title})} className="text-red-600 text-xs inline-flex items-center gap-1"><Trash2 className="h-3 w-3" />Eliminar</button>
            <a href={f.pdf_url} target="_blank" rel="noreferrer" className="text-indigo-600 text-sm" title="Abrir PDF">Ver PDF</a>
          </div>
        </div>
        {(f.rejected_comment && f.status !== 'approved') && (
          <button onClick={()=>openCorrectionsModal(f.id)} className="mt-2 text-xs text-red-600 underline inline-flex items-center gap-1"><ImageIcon className="h-3 w-3" />Ver correcciones</button>
        )}
        {canMove && !(f.status === 'approved' || (f.rejected_comment && f.status !== 'approved')) && (
          <div className="mt-3 flex gap-2">
            <Button size="sm" icon={<CheckCircle className="h-4 w-4" />} onClick={(e)=>{ e.stopPropagation(); setApproveModal({ open: true, ficha: f }); }}>Aprobar</Button>
            <Button size="sm" variant="outline" icon={<AlertTriangle className="h-4 w-4" />} onClick={(e)=>{ e.stopPropagation(); setRejectModal({ open: true, ficha: f }); }}>Desaprobar</Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Aprobación de fichas</h1>
          <div className="flex items-center gap-2">
            {userRole === 'Administrador' && (
              <Button variant="outline" onClick={async ()=>{ const items = await fetchFichasHistoryAll(); setAllHistory(items||[]); setAnalyticsOpen(true); }}>Analítica</Button>
            )}
          {canCreate && (
            <label className="inline-flex items-center gap-2 cursor-pointer text-sm bg-black text-white px-3 py-2 rounded-md">
              <Upload className="h-4 w-4" /> Subir ficha
              <input type="file" accept="application/pdf" className="hidden" onChange={startUploadFlow} disabled={isUploading} />
            </label>
          )}
          </div>
        </div>
        <div className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Periodo</label>
                <select className="w-full px-2 py-2 border rounded" value={timeFilter} onChange={(e)=>setTimeFilter(e.target.value as any)}>
                  <option value="7d">Últimos 7 días</option>
                  <option value="30d">Últimos 30 días</option>
                  <option value="90d">Últimos 90 días</option>
                  <option value="all">Todos</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Tipo</label>
                <select className="w-full px-2 py-2 border rounded" value={kindFilter} onChange={(e)=>setKindFilter(e.target.value as any)}>
                  <option value="all">Todos</option>
                  <option value="produccion">Producción</option>
                  <option value="muestra">Muestra</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Vendedor (email)</label>
                <input className="w-full px-3 py-2 border rounded" placeholder="Buscar por vendedor" value={sellerFilter} onChange={(e)=>setSellerFilter(e.target.value)} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader title="En Revisión" icon={<Clock className="h-4 w-4" />} />
          <CardContent>
            <div className="space-y-2">
              {columns.in_review.map(renderCard)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader title="Aprobada" icon={<CheckCircle className="h-4 w-4" />} />
          <CardContent>
            <div className="space-y-2">
              {columns.approved.map(renderCard)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal de carga */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">Nueva ficha</h3>
              <button onClick={() => setShowUploadModal(false)} className="text-gray-500 hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>
              <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Nombre</label>
                <input value={uploadTitle} onChange={(e)=>setUploadTitle(e.target.value)} className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" placeholder="Nombre de la ficha" />
              </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Tipo</label>
                  <select value={uploadKind} onChange={(e)=>setUploadKind(e.target.value as any)} className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white">
                    <option value="produccion">Producción</option>
                    <option value="muestra">Muestra</option>
                    <option disabled>—</option>
                  </select>
                </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Archivo: {uploadFile?.name}</div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={()=>setShowUploadModal(false)}>Cancelar</Button>
                <Button onClick={confirmUpload} disabled={isUploading}>{isUploading ? 'Subiendo...' : 'Subir'}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal aprobación */}
      {approveModal.open && approveModal.ficha && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">Aprobar ficha</h3>
              <button onClick={()=>setApproveModal({open:false, ficha:null})} className="text-gray-500 hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm mb-4">¿Confirmás la aprobación de <span className="font-semibold">{approveModal.ficha.title}</span>?</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={()=>setApproveModal({open:false, ficha:null})} disabled={isUpdatingStatus}>Cancelar</Button>
              <Button onClick={handleApprove} disabled={isUpdatingStatus}>
                {isUpdatingStatus ? 'Aprobando...' : 'Aprobar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal desaprobación */}
      {rejectModal.open && rejectModal.ficha && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">Desaprobar ficha</h3>
              <button onClick={()=>setRejectModal({open:false, ficha:null})} className="text-gray-500 hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <p className="text-sm">Indicá las correcciones para <span className="font-semibold">{rejectModal.ficha.title}</span>:</p>
              <textarea value={rejectComment} onChange={(e)=>setRejectComment(e.target.value)} rows={4} className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white" placeholder="Correcciones / comentarios" />
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Adjuntar imágenes (opcional)</label>
                <input type="file" accept="image/*" multiple onChange={onRejectImagesChange} />
                {rejectImages.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {rejectImages.map((f,i)=> (
                      <div key={i} className="text-xs text-gray-500 truncate">{f.name}</div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={()=>setRejectModal({open:false, ficha:null})} disabled={isUpdatingStatus}>Cancelar</Button>
                <Button onClick={handleReject} disabled={isUpdatingStatus}>
                  {isUpdatingStatus ? 'Desaprobando...' : 'Desaprobar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal historial */}
      {historyModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 w-full max-w-2xl rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">Historial de la ficha</h3>
              <button onClick={()=>setHistoryModal({open:false, items:[]})} className="text-gray-500 hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {historyModal.items.length === 0 ? (
                <div className="text-sm text-gray-500">Sin movimientos</div>
              ) : (
                <ul className="space-y-2 text-sm">
                  {historyModal.items.map((it:any, idx:number)=> {
                    const meta = getStatusMeta(it.status);
                    return (
                      <li key={idx} className="border rounded p-3">
                        <div className={`flex items-center gap-2 font-medium ${meta.color}`}>
                          {meta.icon}
                          <span>{meta.label}</span>
                        </div>
                        {it.comment && <div className="mt-1 text-gray-700 dark:text-gray-300">{it.comment}</div>}
                        <div className="mt-1 text-xs text-gray-500">{new Date(it.created_at).toLocaleString('es-AR')}</div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmación eliminar */}
      {deleteConfirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">Eliminar ficha</h3>
              <button onClick={()=>setDeleteConfirm({open:false, id:null, title:''})} className="text-gray-500 hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm mb-4">¿Confirmás eliminar <span className="font-semibold">{deleteConfirm.title}</span>?</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={()=>setDeleteConfirm({open:false, id:null, title:''})}>Cancelar</Button>
              <Button onClick={async ()=>{ if(deleteConfirm.id){ await deleteFicha(deleteConfirm.id); } setDeleteConfirm({open:false, id:null, title:''}); }}>Eliminar</Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmación reemplazo */}
      {replaceDone.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">Ficha reemplazada</h3>
              <button onClick={()=>setReplaceDone({open:false, title:''})} className="text-gray-500 hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm mb-4">Se reemplazó la ficha <span className="font-semibold">{replaceDone.title}</span>. La tarjeta volvió a En revisión y queda lista para aprobar o desaprobar.</p>
            <div className="flex justify-end">
              <Button onClick={()=>setReplaceDone({open:false, title:''})}>Entendido</Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ver correcciones */}
      {correctionsModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 w-full max-w-2xl rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">Correcciones</h3>
              <button onClick={()=>setCorrectionsModal({open:false, ficha:null, items:[]})} className="text-gray-500 hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto space-y-3">
              {correctionsModal.items.length === 0 ? (
                <div className="text-sm text-gray-500">Sin correcciones</div>
              ) : (
                correctionsModal.items.map((it:any, idx:number)=> (
                  <div key={idx} className="border rounded p-2">
                    <div className="text-sm text-red-600 font-medium mb-1">{new Date(it.created_at).toLocaleString('es-AR')}</div>
                    {it.comment && <div className="text-sm mb-2">{it.comment}</div>}
                    {Array.isArray(it.image_urls) && it.image_urls.length>0 && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {it.image_urls.map((u:string,i:number)=> (
                          <a key={i} href={u} target="_blank" rel="noreferrer"><img src={u} className="w-full h-24 object-cover rounded" /></a>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Analítica (solo Admin) */}
      {analyticsOpen && userRole === 'Administrador' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 w-full max-w-3xl rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">Analítica de tiempos</h3>
              <button onClick={()=>setAnalyticsOpen(false)} className="text-gray-500 hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Periodo</label>
                <select className="w-full px-2 py-2 border rounded" value={analyticsPeriod} onChange={(e)=>setAnalyticsPeriod(e.target.value as any)}>
                  <option value="7d">Últimos 7 días</option>
                  <option value="30d">Últimos 30 días</option>
                  <option value="90d">Últimos 90 días</option>
                  <option value="all">Todos</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Vendedor (email)</label>
                <input className="w-full px-3 py-2 border rounded" placeholder="Filtrar por vendedor" value={analyticsSeller} onChange={(e)=>setAnalyticsSeller(e.target.value)} />
              </div>
            </div>
            <AnalyticsContent items={allHistory} seller={analyticsSeller} period={analyticsPeriod} />
          </div>
        </div>
      )}
    </div>
  );
};

export default FichasAprobacion;

// Función para calcular tiempo de trabajo hábil (lunes a viernes, 9 a 17 hs)
const calculateBusinessHours = (startDate: Date, endDate: Date): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Si la fecha de inicio es después del final, retornar 0
  if (start >= end) return 0;
  
  let totalHours = 0;
  const current = new Date(start);
  
  // Ajustar la hora de inicio si está fuera del horario laboral
  if (current.getHours() < 9) {
    current.setHours(9, 0, 0, 0);
  } else if (current.getHours() >= 17) {
    // Si es después de las 17, mover al siguiente día hábil a las 9
    current.setDate(current.getDate() + 1);
    current.setHours(9, 0, 0, 0);
    // Si cae en fin de semana, mover al lunes
    while (current.getDay() === 0 || current.getDay() === 6) {
      current.setDate(current.getDate() + 1);
    }
  }
  
  while (current < end) {
    // Verificar si es día hábil (lunes a viernes)
    const dayOfWeek = current.getDay();
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      // Calcular las horas del día
      const dayStart = new Date(current);
      dayStart.setHours(9, 0, 0, 0);
      
      const dayEnd = new Date(current);
      dayEnd.setHours(17, 0, 0, 0);
      
      // Si es el último día, usar la fecha de fin
      const actualDayEnd = end < dayEnd ? end : dayEnd;
      const actualDayStart = start > dayStart ? start : dayStart;
      
      // Solo contar si hay tiempo válido en este día
      if (actualDayStart < actualDayEnd && actualDayStart >= dayStart) {
        const hoursInDay = (actualDayEnd.getTime() - actualDayStart.getTime()) / (1000 * 60 * 60);
        totalHours += Math.max(0, hoursInDay);
      }
    }
    
    // Mover al siguiente día a las 9 AM
    current.setDate(current.getDate() + 1);
    current.setHours(9, 0, 0, 0);
  }
  
  return totalHours;
};

// ----- Subcomponent: AnalyticsContent -----
const AnalyticsContent: React.FC<{items:any[]; seller:string; period:'7d'|'30d'|'90d'|'all'}> = ({ items, seller, period }) => {
  const now = new Date();
  const from = new Date(now);
  if (period === '7d') from.setDate(from.getDate()-7);
  else if (period === '30d') from.setDate(from.getDate()-30);
  else if (period === '90d') from.setDate(from.getDate()-90);
  else from.setFullYear(1970);

  // Necesitamos las fichas para conocer email del vendedor y grupal por vendedor
  const { fichas } = useAppStore();
  const byId: Record<string, any> = (Array.isArray(fichas) ? fichas : []).reduce((acc:any, f:any) => { acc[f.id] = f; return acc; }, {});

  const filtered = (items || []).filter((h:any)=> new Date(h.created_at) >= from)
    .filter((h:any)=> {
      const f = byId[h.ficha_id];
      if (!f) return false;
      if (seller) return (f.vendedor_email||'').toLowerCase().includes(seller.toLowerCase());
      return true;
    });

  // Métrica 1: tiempo de reemplazo por vendedor (desde rejected -> siguiente in_review con comment "Reemplazo de archivo")
  const replacementsBySeller: Record<string, number[]> = {};
  // Métrica 2: tiempo de aprobación/desaprobación de Producción desde carga inicial (primer in_review con "Carga inicial" -> approved o rejected)
  const decisionTimesBySeller: Record<string, number[]> = {};

  // Agrupar por ficha
  const grouped: Record<string, any[]> = {};
  for (const h of filtered) {
    (grouped[h.ficha_id] ||= []).push(h);
  }
  Object.keys(grouped).forEach(fid => grouped[fid].sort((a,b)=> new Date(a.created_at).getTime() - new Date(b.created_at).getTime()));

  for (const fid of Object.keys(grouped)) {
    const history = grouped[fid];
    const ficha = byId[fid];
    const sellerEmail = ficha?.vendedor_email || '—';

    // Reemplazo: buscar última (o una) transición rejected -> in_review con comentario "Reemplazo de archivo"
    const rejected = history.find((h:any)=> h.status === 'rejected');
    const replaced = history.find((h:any)=> h.status === 'in_review' && (h.comment||'').toLowerCase().includes('reemplazo'));
    if (rejected && replaced) {
      const startDate = new Date(rejected.created_at);
      const endDate = new Date(replaced.created_at);
      const businessHours = calculateBusinessHours(startDate, endDate);
      (replacementsBySeller[sellerEmail] ||= []).push(businessHours);
    }

    // Decisión de Producción: desde Carga inicial (in_review con "Carga inicial") al primer approved o rejected
    const initial = history.find((h:any)=> h.status === 'in_review' && (h.comment||'').toLowerCase().includes('carga inicial'));
    const decided = history.find((h:any)=> h.status === 'approved' || h.status === 'rejected');
    if (initial && decided) {
      const startDate = new Date(initial.created_at);
      const endDate = new Date(decided.created_at);
      const businessHours = calculateBusinessHours(startDate, endDate);
      (decisionTimesBySeller[sellerEmail] ||= []).push(businessHours);
    }
  }

  const avg = (arr:number[]) => arr.length ? arr.reduce((a,b)=>a+b,0)/arr.length : 0;
  
  // Función para formatear el tiempo (horas o minutos)
  const formatTime = (hours: number): string => {
    if (hours < 1) {
      const minutes = Math.round(hours * 60);
      return `${minutes} min`;
    }
    return `${hours.toFixed(1)} h`;
  };

  const sellers = Array.from(new Set([ ...Object.keys(replacementsBySeller), ...Object.keys(decisionTimesBySeller) ]));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border rounded p-3">
          <div className="font-semibold mb-2">Tiempo promedio de reemplazo (Vendedor)</div>
          {sellers.length === 0 ? (
            <div className="text-sm text-gray-500">Sin datos en el periodo</div>
          ) : (
            <ul className="text-sm space-y-1">
              {sellers.map(s => (
                <li key={s} className="flex justify-between"><span className="truncate mr-2">{s}</span><span className="font-medium">{formatTime(avg(replacementsBySeller[s]||[]))}</span></li>
              ))}
            </ul>
          )}
        </div>
        <div className="border rounded p-3">
          <div className="font-semibold mb-2">Tiempo promedio decisión de Producción (desde carga)</div>
          {sellers.length === 0 ? (
            <div className="text-sm text-gray-500">Sin datos en el periodo</div>
          ) : (
            <ul className="text-sm space-y-1">
              {sellers.map(s => (
                <li key={s} className="flex justify-between"><span className="truncate mr-2">{s}</span><span className="font-medium">{formatTime(avg(decisionTimesBySeller[s]||[]))}</span></li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="text-xs text-gray-500">
        Promedios calculados en el periodo seleccionado. 
        <br />
        Tiempos consideran únicamente días hábiles (lunes a viernes) de 9:00 a 17:00 hs.
        <br />
        Se muestran en minutos cuando es menos de 1 hora, en horas cuando es 1 hora o más.
      </div>
    </div>
  );
};